import FieldType from "@/utils/constants/FieldType.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import DateRangePreset from "@/utils/constants/DateRangePreset.ts";
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import QuestionField = namaTypes.QuestionField;

const DATE_RANGE_SEPARATOR = "@";

export default class CrossFilterRenderer {
  static renderValue(filledQuestion: DTOFilledQuestionField | undefined, questionField: QuestionField | undefined): string {
    const value = filledQuestion?.value;
    if (ObjectChecker.isEmptyOrNull(value))
      return "";
    // A showAsDateRange cross-filter is served with fieldType Text; its value is still a date range.
    if (questionField?.showAsDateRange)
      return CrossFilterRenderer.renderDateOrRange(value, d => DateFieldUtils.dateToString(d));
    switch (questionField?.fieldType) {
      case FieldType.Reference:
      case FieldType.Genericreference:
        return CrossFilterRenderer.renderReference(value);
      case FieldType.Enum:
        return CrossFilterRenderer.renderEnum(value, questionField?.metaData?.enumTypeName);
      case FieldType.Boolean:
        return CrossFilterRenderer.renderBoolean(value);
      case FieldType.Date:
        return CrossFilterRenderer.renderDateOrRange(value, d => DateFieldUtils.dateToString(d));
      case FieldType.DateAndTime:
        return CrossFilterRenderer.renderDateOrRange(value, d => DateFieldUtils.dateAndTimeToString(d));
      case FieldType.Time:
        return DateFieldUtils.timeToString(value);
      case FieldType.Decimal:
        return CrossFilterRenderer.renderDecimal(value);
      case FieldType.Integer:
      case FieldType.Long:
        return CrossFilterRenderer.renderNumeric(value);
      default:
        return value ?? "";
    }
  }

  static renderLabel(questionField: QuestionField | undefined): string {
    const id = questionField?.id;
    if (!id) return "";
    return Translator.translateId(id);
  }

  static iconFor(questionField: QuestionField | undefined): string | undefined {
    if (questionField?.showAsDateRange) return "event";
    return CrossFilterRenderer.iconForType(questionField?.fieldType);
  }

  static iconForType(fieldType: string | undefined): string | undefined {
    switch (fieldType) {
      case FieldType.Reference:
      case FieldType.Genericreference:
        return "link";
      case FieldType.Enum:
        return "list";
      case FieldType.Boolean:
        return "check_box";
      case FieldType.Date:
      case FieldType.DateAndTime:
        return "event";
      case FieldType.Time:
        return "schedule";
      case FieldType.Decimal:
      case FieldType.Integer:
      case FieldType.Long:
        return "tag";
      default:
        return undefined;
    }
  }

  // showAsDateRange values arrive either as a preset key (ThisMonth, Last30, ...) or in the
  // "from@to" wire form, where either side may be empty for a half-open range.
  private static renderDateOrRange(value: string, formatDate: (d: Date) => string): string {
    if (CrossFilterRenderer.isDateRangePreset(value))
      return Translator.translate(value);
    if (!value.includes(DATE_RANGE_SEPARATOR))
      return CrossFilterRenderer.formatIsoDate(value, formatDate);
    const [from, to] = value.split(DATE_RANGE_SEPARATOR);
    const fromText = ObjectChecker.isNotEmptyOrNull(from) ? CrossFilterRenderer.formatIsoDate(from, formatDate) : "";
    const toText = ObjectChecker.isNotEmptyOrNull(to) ? CrossFilterRenderer.formatIsoDate(to, formatDate) : "";
    if (fromText && toText) return fromText + " – " + toText;
    if (fromText) return "≥ " + fromText;
    if (toText) return "≤ " + toText;
    return "";
  }

  private static isDateRangePreset(value: string): boolean {
    return Object.values(DateRangePreset).includes(value);
  }

  private static formatIsoDate(value: string, formatDate: (d: Date) => string): string {
    const parsed = dayjs(value, ISO_DATE_FORMATS);
    return parsed.isValid() ? formatDate(parsed.toDate()) : value;
  }

  private static renderReference(value: string): string {
    const parsed = FieldTypesUtils.parseString(value, FieldType.Reference);
    if (!parsed || typeof parsed !== "object") return value;
    return Translator.translateRef(parsed) || value;
  }

  private static renderEnum(value: string, enumTypeName: string | undefined): string {
    if (enumTypeName)
      return Translator.translateId(enumTypeName + "." + value) || value;
    const translatedValues = [];
    value.split(CommonConstants.LIST_PARAMS_SEP).forEach(v => translatedValues.push(Translator.translateId(v)));
    return StringUtils.toCSVLineWithSep(",", translatedValues);
  }

  private static renderBoolean(value: string): string {
    if (value === "true" || value === "1") return Translator.translateId("yes") ?? "";
    if (value === "false" || value === "0") return Translator.translateId("no") ?? "";
    return value;
  }

  private static renderDecimal(value: string): string {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4});
  }

  private static renderNumeric(value: string): string {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString();
  }
}
